import {
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import {
  getSessionConfig,
  clearStoredTokens,
} from "../../utils/sessionManager";
import { IdleTimeoutModal } from "../IdleTimeoutModal";

interface InactivityTrackerProps {
  children: ReactNode;
}

const InactivityTracker: React.FC<InactivityTrackerProps> = ({ children }) => {
  // State for idle timeout management
  const [isIdleWarningShown, setIsIdleWarningShown] = useState(false);
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());

  // Use ref to avoid recreating updateActivity on every render
  const isIdleWarningShownRef = useRef(isIdleWarningShown);

  // Timer refs
  const idleTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);

  const config = getSessionConfig();

  // Update ref when state changes
  useEffect(() => {
    isIdleWarningShownRef.current = isIdleWarningShown;
  }, [isIdleWarningShown]);

  /**
   * Calculate time remaining for the user to respond
   * When modal is shown, this shows countdown until automatic logout
   * When modal is not shown, this shows time until warning appears
   */
  const calculateTimeRemaining = (): number => {
    const now = Date.now();
    const timeSinceActivity = now - lastActivityTime;

    if (isIdleWarningShown) {
      // Modal is shown: countdown until automatic logout
      // User has warningModalTime seconds to respond
      const timeUntilLogout = Math.max(
        0,
        Math.floor((config.idleTimeout * 1000 - timeSinceActivity) / 1000),
      );
      return timeUntilLogout;
    } else {
      // Modal not shown: time until warning appears
      const timeUntilWarning = Math.max(
        0,
        Math.floor(
          ((config.idleTimeout - config.warningModalTime) * 1000 -
            timeSinceActivity) /
            1000,
        ),
      );
      return timeUntilWarning;
    }
  };

  /**
   * Clear all timers
   */
  const clearTimers = useCallback(() => {
    if (idleTimeoutId.current) {
      clearTimeout(idleTimeoutId.current);
      idleTimeoutId.current = null;
    }
    if (warningTimeoutId.current) {
      clearTimeout(warningTimeoutId.current);
      warningTimeoutId.current = null;
    }
  }, []);

  /**
   * Schedule idle timeout warning and logout
   */
  const scheduleIdleTimeout = useCallback(() => {
    // Clear existing timers
    clearTimers();

    // Schedule warning modal (idle timeout - warning time)
    const warningDelay = (config.idleTimeout - config.warningModalTime) * 1000;

    warningTimeoutId.current = setTimeout(() => {
      // Showing idle timeout warning modal - user has ${config.warningModalTime}s to respond
      setIsIdleWarningShown(true);
    }, warningDelay);

    // Schedule actual idle logout
    const idleDelay = config.idleTimeout * 1000;

    idleTimeoutId.current = setTimeout(() => {
      // Idle timeout reached - user will be logged out
      setIsIdleWarningShown(false);
      clearStoredTokens();
      window.location.href = "/logout";
    }, idleDelay);
  }, [config.idleTimeout, config.warningModalTime, clearTimers]);

  /**
   * Update activity time and reset idle timer
   * Now stable - no dependencies on changing state
   */
  const updateActivity = useCallback(() => {
    const now = Date.now();
    setLastActivityTime(now);

    // Use ref instead of state variable
    if (isIdleWarningShownRef.current) {
      // User activity detected - dismissing idle warning
      setIsIdleWarningShown(false);
    }

    scheduleIdleTimeout();
  }, [scheduleIdleTimeout]);

  /**
   * Handle user extending session
   */
  const handleExtendSession = () => {
    // Session extended by user action"
    updateActivity();
  };

  /**
   * Handle user choosing to logout
   */
  const handleLogout = () => {
    // User chose to logout from idle timeout modal
    clearTimers();
    setIsIdleWarningShown(false);
    clearStoredTokens();
    window.location.href = "/logout";
  };

  // Setup activity listeners
  useEffect(() => {
    const events = ["mousedown", "keypress", "touchstart", "click"];

    events.forEach((event) => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Initialize idle timeout
    scheduleIdleTimeout();

    return () => {
      // Cleanup event listeners
      events.forEach((event) => {
        document.removeEventListener(event, updateActivity);
      });

      // Clear timers
      clearTimers();
    };
  }, [updateActivity, scheduleIdleTimeout, clearTimers]);

  return (
    <>
      {children}

      {/* Idle Timeout Warning Modal */}
      <IdleTimeoutModal
        isOpen={isIdleWarningShown}
        onExtendSession={handleExtendSession}
        onLogout={handleLogout}
        timeRemaining={calculateTimeRemaining()}
      />
    </>
  );
};

export default InactivityTracker;
